import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { postEditInitialText } from "../../../hooks/useClientPostManage";
import { useIsMobile } from "../../../hooks/useMediaQuery";
import {
  CLUB_LIFE_ADMIN_COMPOSER_CATEGORIES,
  clubLifeCategoryMeta,
} from "../../../constants/clubLifeCategories";
import { DesktopDialog, HubSheetActions } from "../AdminPortal/HubPwa";
import { Chip, ChipGroup } from "../../ui";
import s from "../clinic.module.css";

function isRepostPost(post) {
  return Boolean(post?.repost_source || post?.repost_of_id);
}

export default function ClubLifeEditPostSheet({
  post,
  open,
  busy = false,
  onClose,
  onSave,
  allowCategory = false,
}) {
  const isMobile = useIsMobile(768);
  const [body, setBody] = useState("");
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("event");
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open || !post) return;
    setBody(postEditInitialText(post));
    setTitle(String(post.title || ""));
    setCategory(post.category || "event");
    const t = window.setTimeout(() => inputRef.current?.focus(), 120);
    return () => window.clearTimeout(t);
  }, [open, post]);

  useEffect(() => {
    if (!open || !isMobile) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape" && !busy) onClose?.();
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, busy, onClose, isMobile]);

  if (!open || !post) return null;

  const repost = isRepostPost(post);
  const showTitle = !repost && post.scope !== "wall" && (post.title || post.category !== "tip");
  // Тип записи можно сменить и на стене, и в ленте клуба
  const showCategory = !repost;
  const composerCats = (() => {
    const base = CLUB_LIFE_ADMIN_COMPOSER_CATEGORIES;
    if (!category || base.some((c) => c.id === category)) return base;
    const meta = clubLifeCategoryMeta(category);
    return [...base, { id: meta.id, label: meta.label, icon: meta.icon }];
  })();
  const dialogTitle = repost ? "Комментарий" : "Редактирование";

  const submit = () => {
    if (busy) return;
    const text = body.trim();
    if (!repost && !text) return;
    const payload = { body: repost && !text ? " " : text };
    if (showTitle) {
      payload.title = title.trim() || null;
    }
    if (showCategory) {
      payload.category = category;
    }
    onSave?.(post, payload);
  };

  const formFields = (
    <>
      {repost ? (
        <p className={s.postEditSheetHint}>
          Можно изменить только ваш комментарий к пересланной записи.
        </p>
      ) : null}

      {showCategory ? (
        <div className={s.postEditSheetCategory}>
          <p className={s.postEditSheetCategoryLabel}>Тип записи</p>
          <ChipGroup className={s.postEditSheetChips} label="Тип публикации">
            {composerCats.map((c) => (
              <Chip
                key={c.id}
                selected={category === c.id}
                onClick={() => setCategory(c.id)}
              >
                <span aria-hidden>{c.icon} </span>
                {c.label}
              </Chip>
            ))}
          </ChipGroup>
        </div>
      ) : null}

      {showTitle ? (
        <input
          type="text"
          className={s.postEditSheetTitleInput}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Заголовок"
          maxLength={200}
          disabled={busy}
        />
      ) : null}

      <textarea
        ref={inputRef}
        className={s.postEditSheetInput}
        rows={repost ? 3 : 5}
        value={body}
        onChange={(e) => setBody(e.target.value)}
        placeholder={
          repost
            ? "Комментарий к записи…"
            : "Текст записи"
        }
        maxLength={5000}
        disabled={busy}
      />
    </>
  );

  if (!isMobile) {
    return (
      <DesktopDialog
        open={open}
        onClose={() => !busy && onClose?.()}
        title={dialogTitle}
        bodyClassName={s.postEditDialogBody}
        footer={
          <HubSheetActions
            primary={busy ? "Сохранение…" : "Сохранить"}
            secondaryLabel="Отмена"
            onSecondary={onClose}
            onPrimary={submit}
            primaryDisabled={busy || (!repost && !body.trim())}
            primaryType="button"
          />
        }
      >
        {formFields}
      </DesktopDialog>
    );
  }

  return createPortal(
    <div
      className={s.postActionSheetOverlay}
      role="dialog"
      aria-modal="true"
      aria-labelledby="edit-post-title"
      onClick={() => !busy && onClose?.()}
    >
      <div
        className={s.postEditSheet}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={s.postActionSheetHandle} aria-hidden />

        <header className={s.postEditSheetHeader}>
          <button
            type="button"
            className={s.postEditSheetHeaderBtn}
            onClick={onClose}
            disabled={busy}
          >
            Отмена
          </button>
          <h2 id="edit-post-title" className={s.postEditSheetTitle}>
            {repost ? "Комментарий" : "Редактирование"}
          </h2>
          <button
            type="button"
            className={`${s.postEditSheetHeaderBtn} ${s.postEditSheetHeaderSave}`}
            onClick={submit}
            disabled={busy || (!repost && !body.trim())}
          >
            {busy ? "…" : "Сохранить"}
          </button>
        </header>

        {formFields}
      </div>
    </div>,
    document.body,
  );
}
